import React, { useEffect } from "react";
import InputWrapper from "../../citizen/AVPForms/layout/inputWrapper";
import { Table, TableContainer, Checkbox } from "@mui/material";
import { Button } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";

const CompoundingTable = ({
  paymentHeads,
  mdmsPaymentHeads,
  compoundingFeeHeads,
  compoundingData,
  formData,
  activeCategory,
  onCompoundingSubmit,
  onClose,
}) => {
  const { control, handleSubmit, watch, reset, setValue } = useForm({
    defaultValues: {
      compoundingHeads: [],
    },
  });

  const plotArea = (formData && formData.additionalDetails && formData.additionalDetails.plotArea) || 0;

  const isPlotAreaUpto150 = plotArea <= 150;
  const isPlotAreaUpto300 = plotArea > 150 && plotArea <= 300;
  const isPlotAreaAbove300 = plotArea > 300 && plotArea <= 500;
  const isPlotAreaAbove500 = plotArea > 500;

  const getSlabRateForNewFields = (slabs) => {
    if (!Array.isArray(slabs)) return 0;
    const target = slabs.find((s) => {
      const areaStr = s.area; 
      if (areaStr.indexOf("+") !== -1) {
        const min = parseFloat(areaStr.replace("+", ""));
        return plotArea >= min;
      } else {
        const parts = areaStr.split("-").map(Number);
        const min = parts[0];
        const max = parts[1];
        return plotArea >= min && plotArea <= max;
      }
    });
    return target && target.types && target.types[0] ? target.types[0].rate : 0;
  };

  const rows = [];

  if (Array.isArray(compoundingFeeHeads)) {
    compoundingFeeHeads.forEach((section, index) => {
      if (!section || !section.label) return;

      const isNewJSONHead =
        section.hasOwnProperty("buildUpArea") ||
        section.key === "site_inspection_fees" ||
        section.key === "stacking_charges" ||
        section.key === "security_deposit_tree_plantation";

      if (isNewJSONHead) {
        let dynamicRate = section.rate !== undefined ? section.rate : 0;
        if (section.slabs) {
          dynamicRate = getSlabRateForNewFields(section.slabs);
        }

        rows.push({
          sn: index + 1,
          label: section.label,
          key: section.key,
          type: "new_field",
          unit: section.unit || "Sqmt",
          rate: dynamicRate,
          buildUpArea: section.buildUpArea,
          remarks: section.remarks || "",
          types: null,
        });
        return;
      }

      rows.push({
        sn: index + 1,
        label: section.label,
        key: section.key,
        type: "header",
        unit: section.unit,
        formula: section.formula,
        remarks: section.remarks,
        types: section.types,
      });

      if (Array.isArray(section.slabs) && section.slabs.length > 0) {
        section.slabs.forEach((slab, index1) => {
          rows.push({
            sn: String.fromCharCode(65 + index1),
            label: "Plot area " + slab.area + " Sqmt.",
            key: section.key,
            type: "slab",
            unit: section.unit,
            rate: slab.types ? slab.types.rate : undefined,
            remarks: section.remarks,
            types: slab.types,
          });
        });
      }

      if (Array.isArray(section.subHeads) && section.subHeads.length > 0) {
        section.subHeads.forEach((sub, idx) => {
          if (sub && sub.label) {
            rows.push({
              sn: String.fromCharCode(65 + idx),
              label: sub.label,
              key: section.key,
              type: "subheader",
              unit: section.unit,
              types: sub.types,
              remarks: section.remarks,
            });
          }
        });
      }
    });
  }

  const shouldRenderRow = (row) => {
    if (row.type !== "slab") return true;
    if (row.label.indexOf("0-150") !== -1) return isPlotAreaUpto150;
    if (row.label.indexOf("151-300") !== -1) return isPlotAreaUpto300;
    if (row.label.indexOf("301-500") !== -1) return isPlotAreaAbove300;
    if (row.label.indexOf("501") !== -1) return isPlotAreaAbove500;
    return true;
  };

  const watchHeads = watch("compoundingHeads") || [];

  // Live calculated Total Grand Amount for footer row
  const grandTotalAmount = watchHeads.reduce((sum, item) => {
    if (item && item.amount) {
      return sum + Number(item.amount);
    }
    return sum;
  }, 0);

  // Hook 1: Initial Form Setup
  useEffect(() => {
    if (rows.length) {
      rows.forEach((row, idx) => {
        setValue(`compoundingHeads.${idx}.isChecked`, false);
        setValue(`compoundingHeads.${idx}.quantity`, "");
        setValue(`compoundingHeads.${idx}.categoryRate`, "");
        setValue(`compoundingHeads.${idx}.amount`, "");
        setValue(`compoundingHeads.${idx}.remarks`, "");
      });
    }
  }, [compoundingFeeHeads]);

  // Hook 2: Dynamic Default Rates Mapping
  useEffect(() => {
    rows.forEach((row, idx) => {
      if (row.type === "new_field") {
        setValue(`compoundingHeads.${idx}.categoryRate`, row.rate);
        return;
      }
      if (!Array.isArray(row.types)) return;

      const matched = row.types.find(
        (t) => t.type === activeCategory || t.type === "All"
      );

      if (matched) {
        let rate = "";
        if (matched.rate !== undefined && matched.rate !== null) {
          rate = matched.rate;
        } else if (matched.typeRate !== undefined && matched.typeRate !== null) {
          rate = matched.typeRate;
        } else if (matched.rateA !== undefined && matched.rateA !== null) {
          rate = matched.rateA;
        }
        if (rate !== "") {
          setValue(`compoundingHeads.${idx}.categoryRate`, rate);
        }
      }
    });
  }, [activeCategory, rows.length]);

  // Hook 3: Live Amount Calculation for BOTH types based on Input Values
  useEffect(() => {
    rows.forEach((row, idx) => {
      const currentFormItem = watchHeads[idx];
      if (!currentFormItem) return;

      if (row.type === "new_field") {
        // FIXED: Site Inspection wale rows tabhi calculate honge jab Checked ho aur Quantity valid ho
        if (currentFormItem.isChecked) {
          const qty = Number(currentFormItem.quantity || 0);
          const rateVal = Number(currentFormItem.categoryRate || row.rate || 0);
          const calculatedAmt = qty * rateVal;
          if (Number(currentFormItem.amount) !== calculatedAmt) {
            setValue(`compoundingHeads.${idx}.amount`, calculatedAmt);
          }
        } else {
          if (currentFormItem.amount !== "") {
            setValue(`compoundingHeads.${idx}.amount`, "");
          }
        }
      } else {
        const qty = Number(currentFormItem.quantity || 0);
        const rate = Number(currentFormItem.categoryRate || 0); 
        
        if (qty > 0 && rate >= 0) {
          const calculatedAmt = qty * rate;
          if (Number(currentFormItem.amount) !== calculatedAmt) {
            setValue(`compoundingHeads.${idx}.amount`, calculatedAmt);
          }
        } else {
          if (currentFormItem.amount !== "" && qty === 0) {
            setValue(`compoundingHeads.${idx}.amount`, "");
          }
          setValue(`compoundingHeads.${idx}.remarks`, "");
        }
      }
    });
  }, [watchHeads, rows.length]);

  // Hook 4: Backend Data Edit Sync
  useEffect(() => {
    if (!compoundingData || !rows.length) return;

    rows.forEach((row, idx) => {
      const backendRow = compoundingData.find(
        (item) => item.feeType === row.label || item.key === row.key
      );

      if (backendRow) {
        if (row.type === "new_field") {
          setValue(`compoundingHeads.${idx}.isChecked`, true);
          setValue(`compoundingHeads.${idx}.quantity`, backendRow.quantity || 1);
          setValue(`compoundingHeads.${idx}.amount`, backendRow.amount);
          setValue(`compoundingHeads.${idx}.categoryRate`, backendRow.rate);
          setValue(
            `compoundingHeads.${idx}.remarks`,
            backendRow.remarks || ""
          );
        } else if (backendRow.occupancyType === activeCategory) {
          setValue(`compoundingHeads.${idx}.quantity`, backendRow.quantity);
          setValue(`compoundingHeads.${idx}.amount`, backendRow.amount);
          setValue(`compoundingHeads.${idx}.categoryRate`, backendRow.rate);
          setValue(
            `compoundingHeads.${idx}.remarks`,
            backendRow.remarks || ""
          );
        }
      }
    });
  }, [compoundingData, activeCategory, rows.length]);

  const onSave = (payload) => {
    const compoundingRows = payload.compoundingHeads || [];
console.log(payload.compoundingHeads , "payload.compoundingHeads");
    const finalCompoundingHeads = rows
      .map((row, idx) => {
        const formRow = compoundingRows[idx];

        if (row.type === "new_field") {
          if (!formRow || !formRow.isChecked) return null;
          return {
            taxHeadCode: row.key,
            feeType: row.label,
            quantity: Number(formRow.quantity || 0),
            rate: Number(formRow.categoryRate || row.rate || 0),
            factor: 1,
            amount: Number(formRow.amount || 0),
            remarks: formRow.remarks || row.remarks || "",
            occupancyType: activeCategory,
          };
        } else {
          const quantity = Number(formRow ? formRow.quantity : 0);
          const rate = Number(formRow ? formRow.categoryRate : 0);
          if (!quantity) return null;

          const matchedType = row.types ? row.types.find((t) => t.type === activeCategory) : null;

          return {
            taxHeadCode: matchedType ? matchedType.code : row.key,
            feeType: row.label,
            quantity,
            rate,
            factor: 1,
            amount: Number(formRow ? formRow.amount : 0),
            remarks: formRow ? formRow.remarks : row.remarks || "",
            occupancyType: activeCategory,
          };
        }
      })
      .filter(Boolean);

    if (!finalCompoundingHeads.length) {
      alert("Please enter values or select checkbox to calculate.");
      return;
    }

    onCompoundingSubmit(finalCompoundingHeads);
    console.log(finalCompoundingHeads , "finalCompoundingHeads");
    
  };

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <TableContainer className="table-container">
        <Table stickyHeader className="application-table">
          <thead>
            <tr>
              <th className="fit-table-head">S.N.</th>
              <th className="fit-table-head">Select</th>
              <th className="fit-table-head">Description</th>
              <th className="fit-table-head">Quantity</th>
              <th className="fit-table-head">Unit</th>
              <th className="fit-table-head">Rate</th>
              <th className="fit-table-head">{activeCategory}</th>
              <th className="fit-table-head">Amount (₹)</th>
              <th className="fit-table-head">info</th>
              <th className="fit-table-head">Remarks</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, idx) => {
              if (!shouldRenderRow(row)) return null;

              let rowClass = "";
              if (row.type === "header") rowClass = "row-header";
              else if (row.type === "subheader" || row.type === "slab") rowClass = "row-slab";
              else if (row.type === "new_field") rowClass = "row-new-field";
              else rowClass = "row-empty";

              let rateColumnValue = "";
              let categoryColumnValue = "";

              if (row.type === "new_field") {
                rateColumnValue = row.rate;
                categoryColumnValue = "N/A";
              } else if (Array.isArray(row.types)) {
                const matched = row.types.find(
                  (t) => t.type === activeCategory || t.type === "All"
                );
                if (matched) {
                  if (matched.typeRate !== undefined) categoryColumnValue = matched.typeRate;
                  else if (matched.rateA !== undefined) categoryColumnValue = matched.rateA;
                  if (matched.rate !== undefined) rateColumnValue = matched.rate;
                }
              }

              return (
                <tr key={idx} className={rowClass}>
                  <td>{row.sn}</td>
                  <td>
                    {row.type === "new_field" ? (
                      <Controller
                        name={`compoundingHeads.${idx}.isChecked`}
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            checked={Boolean(field.value)}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{row.label && row.type !== "type" ? row.label : ""}</td>
                  
                  {/* CHANGED: Sabhi fields ke liye input box provide kar diya hai */}
                  <td style={{ width: "12vw" }}>
                    <InputWrapper
                      name={`compoundingHeads.${idx}.quantity`}
                      control={control}
                      type="textInput"
                      placeholder="0"
                      hasLabel={false}
                      width="8vw"
                      height="22px"
                      className="paymentHeadInput"
                    />
                  </td>

                  <td style={{ width: "12vw" }}>{row.unit || ""}</td>
                  
                  <td style={{ width: "12vw" }}>
                    <InputWrapper
                      name={`compoundingHeads.${idx}.categoryRate`}
                      control={control}
                      type="textInput"
                      placeholder="0"
                      hasLabel={false}
                      width="8vw"
                      height="22px"
                      className="paymentHeadInput"
                      disabled={false}
                    />
                  </td>

                  <td>{categoryColumnValue || "1"}</td>

                  <td className="amount-col" style={{ width: "12vw" }}>
                    <InputWrapper
                      name={`compoundingHeads.${idx}.amount`}
                      control={control}
                      type="textInput"
                      hasLabel={false}
                      width="8vw"
                      height="22px"
                      className="paymentHeadInput"
                      disabled 
                    />
                  </td>

                  <td>{row.remarks || row.formula || ""}</td>
                  <td >
                                           <InputWrapper
                           name={`compoundingHeads.${idx}.remarks`}
                          hasLabel={false}
                          control={control}
                          placeholder="Enter Remarks"
                        type="textInput"
                      width="8vw"
                      height="22px"
                      className="paymentHeadInput"
                        />
                                        </td>
                </tr>
              );
            })}
          </tbody>

          {/* Grand Total Footer */}
          <tfoot>
            <tr style={{ backgroundColor: "#f9f9f9", fontWeight: "bold", borderTop: "2px solid #ccc" }}>
              <td colSpan="7" style={{ textAlign: "right", paddingRight: "15px", fontSize: "14px", color: "#333" }}>
                Total Amount:
              </td>
              <td style={{ fontSize: "14px", color: "#157CB3", fontWeight: "700" }}>
                ₹ {grandTotalAmount.toFixed(2)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </Table>
      </TableContainer>
   {/* Action Buttons Section */}
      <div style={{ display: "flex", justifyContent: "center", gap: "15px", margin: "2% 0" }}>
        {/* NEW: Go to Map Submission Button */}
       
        <Button
          variant="contained"
          color="success"
          size="medium"
          type="submit"
          style={{
            backgroundColor: "#157CB3",
            height: "28px",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Calculate
        </Button>
      </div>
    </form>
  );
};

export default CompoundingTable;

